import { getMetrics } from '@sembo-esg/utils';
import { noop } from 'lodash';

import { logs } from '../../utils/logs';
import {
  HistogramObserveParams,
  IncCounterParams,
  LabelsMap,
  MetricArguments,
  SetGaugeParams,
} from './MetricsService.types';

const BASE_NAME = 'marketplace';
const metrics = getMetrics(BASE_NAME);

async function incCounter({ name, labels = {}, value = 1 }: IncCounterParams) {
  const metric = metrics.getCounter(name);
  metric.inc(labels, value);
}

async function setGauge({ name, labels = {}, value }: SetGaugeParams) {
  const metric = metrics.getGauge(name);
  metric.set(labels, value);
}

async function histogramObserve(params: HistogramObserveParams) {
  return histogramObserveSafely(params, () =>
    metrics.getHistogram(params.name)
  );
}

async function histogramObserveSafely(
  {
    value: executionTimeSeconds,
    labels = {},
    success = true,
  }: HistogramObserveParams,
  getHistogram: () => any
) {
  executeSafely(() => {
    const histogram = getHistogram();

    histogram.observe(
      {
        ...labels,
        success: success ? 'true' : 'false',
      },
      executionTimeSeconds
    );
  }, 'Getting or creating histogram failed');
}

async function quantityHistogramObserve(params: HistogramObserveParams) {
  return histogramObserveSafely(params, () =>
    metrics.getQuantityHistogram(params.name)
  );
}

async function durationSecondsHistogramObserve(params: HistogramObserveParams) {
  return histogramObserveSafely(params, () =>
    metrics.getDurationSecondsHistogram(params.name)
  );
}

function executeSafely(inner: () => void, message: string) {
  try {
    inner();
  } catch (e) {
    logs.error(
      {
        message,
        error: e,
      },
      'EXECUTE_SAFELY_ERROR'
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildSafeFunction(inner: (...args: any[]) => void, message: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any[]) => executeSafely(() => inner(...args), message);
}

async function executeWithTiming<R>(
  { name, labels = {} }: MetricArguments,
  callback: () => Promise<R>,
  outputToStd = false
): Promise<R> {
  let stop = noop;

  executeSafely(() => {
    const histogram = metrics.getHistogram(name);

    stop = buildSafeFunction(
      histogram.startTimer(labels),
      'Starting timer failed'
    );
  }, 'Getting or creating histogram failed');

  try {
    const result = await callback();
    stop(
      {
        ...labels,
        success: 'true',
      },
      outputToStd
    );
    return result;
  } catch (err) {
    stop(
      {
        ...labels,
        success: 'false',
      },
      outputToStd
    );
    throw err;
  }
}

async function executeWithTimingGetStaticProps<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'getStaticProps',
      labels,
    },
    callback
  );
}

async function executeWithTimingContentGetPlace<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'content_get_place',
      labels,
    },
    callback
  );
}

async function executeWithTimingContentGetHotel<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'content_get_hotel',
      labels,
    },
    callback
  );
}

async function executeWithTimingExternalContent<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'external_content',
      labels,
    },
    callback
  );
}

async function executeWithTimingSanity<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'sanity',
      labels,
    },
    callback
  );
}

async function executeWithTimingSanityGeneric<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'sanity_generic',
      labels,
    },
    callback
  );
}

async function executeWithTimingContent<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'content',
      labels,
    },
    callback
  );
}

async function executeWithTimingSanityRevalidate<R>(
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'sanity_revalidate',
    },
    callback
  );
}

async function executeWithTimingSanityGetRevalidatePaths<R>(
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'sanity_get_revalidate_paths',
    },
    callback
  );
}

async function executeWithTimingAdyen<R>(
  labels: LabelsMap,
  callback: () => Promise<R>
) {
  return executeWithTiming(
    {
      name: 'adyen',
      labels,
    },
    callback
  );
}

function collectNodeMetrics() {
  metrics.collectNodeMetrics();
}

class Metrics {
  incCounter = incCounter;
  setGauge = setGauge;
  executeWithTiming = executeWithTiming;
  executeWithTimingGetStaticProps = executeWithTimingGetStaticProps;
  executeWithTimingContentGetPlace = executeWithTimingContentGetPlace;
  executeWithTimingContentGetHotel = executeWithTimingContentGetHotel;
  executeWithTimingExternalContent = executeWithTimingExternalContent;
  executeWithTimingSanity = executeWithTimingSanity;
  executeWithTimingSanityGeneric = executeWithTimingSanityGeneric;
  executeWithTimingContent = executeWithTimingContent;
  executeWithTimingSanityRevalidate = executeWithTimingSanityRevalidate;
  executeWithTimingSanityGetRevalidatePaths =
    executeWithTimingSanityGetRevalidatePaths;
  executeWithTimingAdyen = executeWithTimingAdyen;
  histogramObserve = histogramObserve;
  quantityHistogramObserve = quantityHistogramObserve;
  durationSecondsHistogramObserve = durationSecondsHistogramObserve;
  collectNodeMetrics = collectNodeMetrics;
}

export const MetricsService = new Metrics();
